import type { ReactNode } from "react";
import { toast, type ExternalToast } from "sonner";

type ToastDescription = ReactNode | null | undefined;

type ReportToastOptions = Omit<ExternalToast, "description"> & {
    description?: ToastDescription;
};

let toastSeq = 1;
const nextToastId = () => `app_toast_${Date.now().toString(36)}_${toastSeq++}`;

const withDefaults = (description?: ToastDescription, options?: ReportToastOptions): ExternalToast => {
    const id = options?.id || nextToastId();
    return {
        description,
        ...options,
        id,
        testId: `app-toast-${String(id)}`,
        closeButton: false,
        dismissible: false,
    };
};

const errorDescription = (error: unknown, fallback: string) => {
    const responseData = (error as any)?.response?.data;
    if (typeof responseData === "string" && responseData.trim()) return responseData;
    if (typeof responseData?.message === "string" && responseData.message.trim()) return responseData.message;
    if (Array.isArray(responseData?.messages) && responseData.messages.length) {
        const message = responseData.messages[0]?.description || responseData.messages[0]?.message;
        if (message) return message;
    }
    if ((error as any)?.message) return (error as any).message;
    return fallback;
};

export const reportToast = {
    success(message: ReactNode, description?: ToastDescription, options?: ReportToastOptions) {
        return toast.success(message, withDefaults(description, { duration: 4500, ...options }));
    },

    info(message: ReactNode, description?: ToastDescription, options?: ReportToastOptions) {
        return toast.info(message, withDefaults(description, { duration: 5200, ...options }));
    },

    warning(message: ReactNode, description?: ToastDescription, options?: ReportToastOptions) {
        return toast.warning(message, withDefaults(description, { duration: 6500, ...options }));
    },

    error(message: ReactNode, description?: ToastDescription, options?: ReportToastOptions) {
        return toast.error(message, withDefaults(description, { duration: 8500, ...options }));
    },

    loading(message: ReactNode, description?: ToastDescription, options?: ReportToastOptions) {
        return toast.loading(message, withDefaults(description, { duration: Infinity, ...options }));
    },

    promise<T>(
        promise: Promise<T>,
        messages: {
            loading: ReactNode;
            success: ReactNode | ((data: T) => ReactNode);
            error: ReactNode | ((error: unknown) => ReactNode);
            description?: ReactNode | ((data: T) => ReactNode);
        },
        options?: ReportToastOptions,
    ) {
        const defaults = withDefaults(undefined, options);
        return toast.promise(promise, {
            ...defaults,
            loading: messages.loading,
            success: messages.success,
            error: messages.error,
            description: messages.description ?? options?.description,
        });
    },

    updateSuccess(id: string | number, message: ReactNode, description?: ToastDescription, options?: ReportToastOptions) {
        return toast.success(message, withDefaults(description, { id, duration: 4500, ...options }));
    },

    updateError(id: string | number, message: ReactNode, description?: ToastDescription, options?: ReportToastOptions) {
        return toast.error(message, withDefaults(description, { id, duration: 8500, ...options }));
    },

    dismiss(id?: string | number) {
        return toast.dismiss(id);
    },

    messageFromError: errorDescription,
};
